const CRC_TABLE = (() => {
  const table: number[] = [];
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

/**
 * 判断当前浏览器是否支持 gzip 请求压缩所需 API。
 * @returns true 表示可使用 gzip customFetch
 */
export function canUseSnowplowGzipFetch(): boolean {
  return (
    typeof CompressionStream !== 'undefined' &&
    typeof Blob !== 'undefined' &&
    typeof TextEncoder !== 'undefined' &&
    typeof Response !== 'undefined' &&
    typeof btoa !== 'undefined'
  );
}

/**
 * 计算 Uint8Array 的 CRC32。
 * @param bytes - 原始请求体字节
 * @returns 无符号 CRC32
 */
function crc32(bytes: Uint8Array): number {
  let crc = 0 ^ -1;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ bytes[i]) & 0xff];
  }
  return (crc ^ -1) >>> 0;
}

/**
 * 将 fetch body 转为 Uint8Array。
 * @param body - Snowplow 请求体
 * @returns Uint8Array 或 null
 */
async function bodyToBytes(body: unknown): Promise<Uint8Array | null> {
  if (!body) return null;
  if (typeof body === 'string') return new TextEncoder().encode(body);
  if (body instanceof Uint8Array) return body;
  if (body instanceof Blob) return new Uint8Array(await body.arrayBuffer());
  if (body instanceof ArrayBuffer) return new Uint8Array(body);
  return null;
}

/**
 * 将 Uint8Array 转为 base64 字符串。
 * @param bytes - gzip 后的字节
 * @returns base64 字符串
 */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

/**
 * gzip 压缩 Uint8Array。
 * @param bytes - 原始请求体字节
 * @returns gzip 后的字节
 */
async function gzipBytes(bytes: Uint8Array): Promise<Uint8Array> {
  const arrayBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(arrayBuffer).set(bytes);
  const stream = new Blob([arrayBuffer]).stream().pipeThrough(new CompressionStream('gzip'));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * 创建 Snowplow gzip customFetch。
 * @returns 可传入 Snowplow newTracker 的 customFetch
 */
export function createSnowplowGzipFetch(): typeof fetch {
  return async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [input, init] = args;
    try {
      const method = String(init?.method || 'GET').toUpperCase();
      if (method !== 'POST') return fetch(input, init);

      const bytes = await bodyToBytes(init?.body);
      if (!bytes || bytes.length === 0) return fetch(input, init);

      const gzipped = await gzipBytes(bytes);
      const formData = new URLSearchParams();
      formData.set('gzip', '1');
      formData.set('data', bytesToBase64(gzipped));
      formData.set('crc', String(crc32(bytes)));

      return fetch(input, {
        ...init,
        headers: {
          ...(init?.headers as Record<string, string> | undefined),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
    } catch {
      return fetch(input, init);
    }
  };
}
